import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useCloudSyncPaused } from '@/hooks/use-cloud-sync-paused';
import ConflictResolutionPanel from '@/modules/sync/conflict-resolution-panel';
import { getConflictOperations, getPendingOperations } from '@/storage/outbox';
import { useAppState } from '@/store/context';
import { useSyncOptional } from '@/sync/sync-context';
import style from './style.module.scss';
export default function SyncStatusBadge() {
    const sync = useSyncOptional();
    const { state } = useAppState();
    const { paused } = useCloudSyncPaused();
    const [panelOpen, setPanelOpen] = useState(false);
    const [counts, setCounts] = useState({ pending: 0, conflict: 0 });
    const current = state.budgets.find((b) => b.id === state.currentId);
    const currentId = current?.id;
    const syncStatus = sync?.status;
    // Счётчики бэджа — по ТЕКУЩЕМУ бюджету (outbox-записи фильтруем по budgetId): индикатор и
    // модалка относятся именно к открытому бюджету, а не ко всем бюджетам пользователя.
    useEffect(() => {
        let alive = true;
        Promise.all([getPendingOperations(), getConflictOperations()])
            .then(([pending, conflict]) => {
            if (!alive)
                return;
            setCounts({
                pending: currentId ? pending.filter((o) => o.budgetId === currentId).length : 0,
                conflict: currentId ? conflict.filter((o) => o.budgetId === currentId).length : 0,
            });
        })
            .catch(() => { });
        return () => {
            alive = false;
        };
    }, [syncStatus, currentId]);
    if (!sync)
        return null;
    // Индикатор нужен только для облачных (CloudBound) бюджетов — они реально
    // синхронизируются между устройствами. Для LocalOnly бюджета, который никуда
    // не синхронизируется, индикатор бессмысленен и сбивает с толку.
    if (current?.storageMode !== 'CloudBound')
        return null;
    // Синхронизация приостановлена (подписка истекла/отозвана/просрочена): движок
    // выключен, поэтому бэдж не должен гореть зелёным «Синхронизировано».
    if (paused) {
        return (_jsx("button", { className: style['badge'], "data-testid": "sync-badge", title: "\u0421\u0438\u043D\u0445\u0440\u043E\u043D\u0438\u0437\u0430\u0446\u0438\u044F \u043F\u0440\u0438\u043E\u0441\u0442\u0430\u043D\u043E\u0432\u043B\u0435\u043D\u0430 \u2014 \u043F\u0440\u043E\u0432\u0435\u0440\u044C\u0442\u0435 \u043F\u043E\u0434\u043F\u0438\u0441\u043A\u0443", children: _jsx("span", { className: style['dot-paused'] }) }));
    }
    const { status } = sync;
    const pendingCount = counts.pending;
    const conflictCount = counts.conflict;
    const hasConflicts = conflictCount > 0;
    const onBadgeClick = () => {
        // Есть конфликты (в этом бюджете) → открываем панель (она сама реконсилит, если сервер пуст).
        // Иначе (в т.ч. в error-state) — ручной ре-синк как путь восстановления.
        if (hasConflicts)
            setPanelOpen(true);
        else
            sync.triggerSync();
    };
    const isActive = status.state === 'pushing' || status.state === 'pulling';
    const isError = status.state === 'error';
    const isIdle = status.state === 'idle';
    const title = isError
        ? `Ошибка синхронизации: ${status.error}`
        : isActive
            ? 'Синхронизация...'
            : [
                pendingCount > 0 ? `Ожидает отправки: ${pendingCount}` : null,
                conflictCount > 0 ? `Конфликтов: ${conflictCount}` : null,
            ]
                .filter(Boolean)
                .join('; ') || 'Синхронизировано';
    return (_jsxs(_Fragment, { children: [_jsxs("button", { className: style['badge'], "data-testid": "sync-badge", onClick: onBadgeClick, title: title, children: [isActive && _jsx("span", { className: style['spinner'] }), isError && _jsx("span", { className: style['dot-error'] }), isIdle && pendingCount === 0 && conflictCount === 0 && _jsx("span", { className: style['dot-ok'] }), isIdle && pendingCount > 0 && _jsx("span", { className: style['count'], children: pendingCount }), hasConflicts && _jsx("span", { className: style['count-conflict'], children: conflictCount })] }), panelOpen && (_jsx(ConflictResolutionPanel, { budgetId: currentId, onClose: () => setPanelOpen(false), fetchConflicts: sync.fetchConflicts, resolveConflict: sync.resolveConflict, onResolved: sync.triggerSync }))] }));
}
